<template>
  <VueTypeaheadBootstrap
    v-model="search"
    @input="searchOption"
    @hit="selectValue"
    :data="formattedItems"
    :placeholder="placeholder"
    :class="[stateClass]"
    :serializer="(item) => item.text"
    :showOnFocus="true"
    :size="size"
  >
    <template #suggestion="{ data, htmlText }">
      <div class="d-flex align-items-center">
        <img
          v-if="data?.imagem?.url"
          class="picture-item"
          :src="data?.imagem?.url"
        />
        <span v-else class="picture-placeholder" />

        <span class="ml-2" v-html="htmlText"></span>
        <b-badge
          v-if="data?.oportunidades_count"
          variant="exitos"
          pill
          class="ml-auto bg-exitos text-white font-weight-normal small"
        >
          {{ data?.oportunidades_count }}
        </b-badge>
      </div>
    </template>
    <template #append>
      <div class="loading-field" v-if="isLoading">
        <b-spinner label="Loading..." />
      </div>
    </template>
  </VueTypeaheadBootstrap>
</template>
<script>
import VueTypeaheadBootstrap from "vue-typeahead-bootstrap";
import { mapActions, mapState } from "vuex";

import {
  formControlMixin,
  props as formControlProps,
} from "bootstrap-vue/src/mixins/form-control";

import {
  formStateMixin,
  props as formStateProps,
} from "bootstrap-vue/src/mixins/form-state";

import { sortKeys } from "bootstrap-vue/src/utils/object";
import { makePropsConfigurable } from "bootstrap-vue/src/utils/props";
import { modelValue } from "@/mixins/modelValue";
import { safeArray, uniq } from "@/helpers/functions/ArrayHelpers";
import debounce from "lodash/debounce";
import find from "lodash/find";
import { deepCopy } from "@/helpers/functions/ObjectHelpers";

const props = makePropsConfigurable(
  sortKeys({
    ...formStateProps,
    ...formControlProps,
    value: {
      type: [String, Number],
      default: "",
    },
    placeholder: {
      type: String,
      default: "Selecionar concedente...",
    },
    size: {
      type: String,
      default: "md",
    },
  })
);

export default {
  components: {
    VueTypeaheadBootstrap,
  },
  mixins: [formControlMixin, formStateMixin, modelValue("value", "change")],
  props,
  data: () => ({
    search: "",
    items: [],
    isLoading: false,
  }),
  computed: {
    ...mapState("concedentes", ["concedente"]),
    formattedItems() {
      return safeArray(deepCopy(this.items)).map((item) => ({
        ...item,
        text: item.nome,
      }));
    },
  },
  methods: {
    ...mapActions("concedentes", ["ActionGetAll", "ActionShow"]),
    formatItem(item) {
      if (!item) return null;

      return {
        ...item,
        text: item.text || item.nome,
      };
    },
    async fetchItemById(id) {
      try {
        this.isLoading = true;
        await this.ActionShow(id);
        return deepCopy(this.concedente);
      } catch (error) {
        return null;
      } finally {
        this.isLoading = false;
      }
    },
    async setById(id) {
      if (!id) {
        this.search = "";
        return;
      }

      const localItems = deepCopy(this.formattedItems);

      const selected = find(localItems, { id: Number(id) });

      if (!selected) {
        const result = await this.fetchItemById(id);
        const item = this.formatItem(result);
        this.items = uniq([...localItems, item]);

        this.search = item.text;
        return;
      }

      this.search = selected.text;
    },
    selectValue($event) {
      this.modelValue = $event?.id;
    },
    searchOption: debounce(async function ($event) {
      this.isLoading = true;
      const result = await this.ActionGetAll({
        page: 1,
        per_page: 10,
        q: $event,
        incluir_sem_oportunidade: true,
      });

      this.items = safeArray(result.data);
      this.isLoading = false;
    }, 500),
  },
  watch: {
    search(value) {
      if (value !== "") return;

      this.modelValue = null;
    },
    modelValue(value) {
      if (!value) {
        this.search = "";
        return;
      }

      this.setById(value);
    },
    formattedItems() {
      if (!this.modelValue) return;

      this.setById(this.modelValue);
    },
  },
  async created() {
    try {
      this.isLoading = true;
      const result = await this.ActionGetAll();

      this.items = safeArray(result.data);
    } finally {
      this.isLoading = false;
    }
  },
};
</script>
<style scoped lang="scss">
.picture-item {
  width: 28px;
  height: 28px;
  border-radius: 0.5rem;
  object-fit: cover;
  aspect-ratio: 1;
}

.picture-placeholder {
  width: 28px;
  height: 28px;
  background-color: unset;
  position: relative;
}

.loading-field {
  border: 1px solid #cad1d7;
  display: inline-flex;
  align-items: center;
  padding-right: 0.5rem;
  border-radius: 0.375rem;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;

  .spinner-border {
    color: var(--exitos);
    width: 1em;
    height: 1em;
    border-width: 0.15rem;
  }
}
</style>
